/**
 * One plan, rendered: its numbers, its days, its road and its stops.
 *
 * Implements Stacked Day Blocks with per-day view mode and collapsible sections:
 * - Each Day section can be collapsed / expanded via its header.
 * - Each Day section has its own contextual toggle button [ 🗺️ Bản đồ ] / [ 📋 Lịch trình ]
 *   placed on the right of the day title row.
 * - In List View: Full timeline with stops, timing, and travel legs.
 * - In Map View: Day-specific map with interactive horizontal bottom carousel.
 * - When clicking a pin on the map, the corresponding card in the carousel smoothly
 *   scrolls to the first visible position and becomes active.
 * - On Desktop: List mode displays Split-View (Timeline left + Map right) with two-way hover and click sync.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import type { PlanDay, PlanVariantDetail } from "@trippool/contracts";

import { copy } from "../copy.ts";
import {
  dayDistancesKnown,
  dayPins,
  formatClock,
  formatDriving,
  formatDrivingMinutes,
  formatKm,
  formatRestHours,
  stopLabel,
  stopMarkers,
} from "../lib/plan.ts";
import { formatDayDate } from "../lib/tripDates.ts";
import { RouteMap } from "./RouteMap.tsx";

export interface PlanRoadmapProps {
  detail: PlanVariantDetail;
  startDate?: string | null;
  hideSummary?: boolean;
}

interface PlanDayBlockProps {
  day: PlanDay;
  /**
   * The trip's start date, normalised to an explicit `string | null` at this boundary.
   *
   * The outer prop is optional because a caller may render a plan with no stored date, but inside here the
   * value is always present and unambiguous: `null` is "no date", never "the caller did not pass one". That
   * distinction is what `exactOptionalPropertyTypes` enforces — an optional prop would make the value
   * `string | null | undefined`, and the inner component would have to guess which of `undefined` and `null`
   * the date rule applies to.
   */
  startDate: string | null;
  daysByPlace: Map<string, number[]>;
}

function PlanDayBlock({ day, startDate, daysByPlace }: PlanDayBlockProps): ReactElement {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isMapMode, setIsMapMode] = useState<boolean>(false);
  const [selectedPin, setSelectedPin] = useState<number | null>(null);
  const [hoveredPin, setHoveredPin] = useState<number | null>(null);

  const carouselRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLOListElement>(null);

  const activePin = selectedPin ?? hoveredPin;

  /*
   * The pins and the legs, memoised on the day.
   *
   * `RouteMap` rebuilds its overlay whenever its props change by identity, and that redraw clears the layer
   * and refits the day — so a `dayPins(day)` call or an inline `legs` array in this render would refit the
   * map on every hover, which is the behaviour the highlight effect explicitly forbids. `Planner.tsx`
   * documents the same rule for `arrangeMapStops` and `arrangeMapLegs`; this is that fix for this component.
   */
  const pins = useMemo(() => dayPins(day), [day]);
  const legs = useMemo(
    () => day.legs.map((leg) => ({ distanceM: leg.distance_m, geometry: leg.geometry })),
    [day],
  );
  const markers = stopMarkers(day.stops);
  const dayDate = formatDayDate(startDate, day.day_index);
  const dayTitle = dayDate ? `Ngày ${day.day_index} – ${dayDate}` : `Ngày ${day.day_index}`;

  const scrollToPin = useCallback((pinNumber: number, mode: "map" | "list") => {
    if (mode === "map" && carouselRef.current) {
      const card = carouselRef.current.querySelector<HTMLElement>(`[data-pin="${pinNumber}"]`);
      if (card) {
        const carousel = carouselRef.current;
        const targetLeft = card.offsetLeft - carousel.offsetLeft;
        carousel.scrollTo({
          left: targetLeft,
          behavior: "smooth",
        });
      }
    } else if (mode === "list" && timelineRef.current) {
      const stopEl = timelineRef.current.querySelector<HTMLElement>(`[data-pin="${pinNumber}"]`);
      if (stopEl) {
        stopEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, []);

  const handleSelectPosition = useCallback(
    (pinNumber: number | null) => {
      setSelectedPin(pinNumber);
      if (pinNumber !== null) {
        scrollToPin(pinNumber, isMapMode ? "map" : "list");
      }
    },
    [isMapMode, scrollToPin],
  );

  useEffect(() => {
    if (selectedPin !== null && isMapMode) {
      const timer = setTimeout(() => {
        scrollToPin(selectedPin, "map");
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isMapMode, selectedPin, scrollToPin]);

  return (
    <section className="plan-day">
      <header className="plan-day__head">
        <div className="plan-day__title-row">
          <button
            type="button"
            className="plan-day__title-btn"
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            <span className="plan-day__chevron" aria-hidden="true">
              {isCollapsed ? "▶" : "▼"}
            </span>
            <h3 className="plan-day__title">
              {dayTitle}
              {day.summary !== null && ` — ${day.summary}`}
            </h3>
          </button>

          <button
            type="button"
            className="plan-day__toggle-btn"
            aria-label={
              isMapMode
                ? `Xem lịch trình Ngày ${day.day_index}`
                : `Xem bản đồ Ngày ${day.day_index}`
            }
            onClick={(e) => {
              e.stopPropagation();
              setIsMapMode((prev) => !prev);
              if (isCollapsed) {
                setIsCollapsed(false);
              }
            }}
          >
            {isMapMode ? "📋 Lịch trình" : "🗺️ Bản đồ"}
          </button>
        </div>

        <p className="plan-day__metrics">
          <span className="badge">
            🚗 {dayDistancesKnown(day) ? formatDrivingMinutes(day.driving_min) : copy.state.unknown}
          </span>
          <span className="badge">
            📍 {day.stops.length} điểm dừng
          </span>
          {dayDistancesKnown(day) && (
            <span className="badge">
              📏 {formatKm(day.distance_m)}
            </span>
          )}
        </p>
      </header>

      {day.unsatisfied.length > 0 && (
        <div className="alert alert--warning" role="alert">
          {day.unsatisfied.join("; ")}
        </div>
      )}

      {/* Day Content: Expandable / Collapsible */}
      {!isCollapsed && (
        <>
          {isMapMode ? (
            /* Day Map View with Horizontal Carousel */
            <div className="plan-day__map-view">
              <div className="plan-day__map-wrapper">
                {pins.length > 0 ? (
                  <RouteMap
                    stops={pins}
                    geometry={day.geometry}
                    legs={legs}
                    distancesKnown={dayDistancesKnown(day)}
                    highlightedPosition={activePin}
                    onHoverPosition={setHoveredPin}
                    onSelectPosition={handleSelectPosition}
                  />
                ) : (
                  <div className="plan-day__empty">Ngày này không có điểm dừng nào trên bản đồ.</div>
                )}
              </div>

              {day.stops.length > 0 && (
                <div
                  ref={carouselRef}
                  className="plan-carousel"
                  role="region"
                  aria-label={`Danh sách điểm dừng Ngày ${day.day_index}`}
                >
                  {day.stops.map((stop, stopIndex) => {
                    const pinNumber = stop.position + 1;
                    const leg = day.legs[stopIndex];
                    const isSelected = pinNumber === activePin;
                    return (
                      <div
                        key={`${stop.position}-${stop.kind}`}
                        data-pin={pinNumber}
                        className={
                          isSelected
                            ? "plan-carousel__card plan-carousel__card--on"
                            : "plan-carousel__card"
                        }
                        onClick={() => handleSelectPosition(pinNumber)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSelectPosition(pinNumber);
                          }
                        }}
                      >
                        <div className="plan-carousel__top">
                          <span className="plan-carousel__num">{markers[stopIndex]}</span>
                          <span className="plan-carousel__title">{stopLabel(stop)}</span>
                        </div>
                        <div className="plan-carousel__meta">
                          <span className="plan-carousel__time">
                            {stop.start_min !== null ? formatClock(stop.start_min) : ""}
                            {stop.duration_min !== null ? ` · ${stop.duration_min} phút` : ""}
                          </span>
                          {leg !== undefined && leg.duration_s !== null && (
                            <span className="plan-carousel__leg">
                              🚗 {formatDriving(leg.duration_s)} di chuyển
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Day List View: Timeline (Split with map on desktop) */
            <div className="plan-day__body">
              <div className="plan-day__timeline">
                <ol ref={timelineRef} className="timeline">
                  {day.stops.map((stop, stopIndex) => {
                    const leg = day.legs[stopIndex];
                    const isVisit = stop.kind === "visit";
                    const pinNumber = stop.position + 1;
                    const isHighlighted = pinNumber === activePin;

                    return (
                      <li
                        key={`${stop.position}-${stop.kind}`}
                        data-pin={pinNumber}
                        className={
                          isHighlighted
                            ? "timeline__stop timeline__stop--highlighted"
                            : "timeline__stop"
                        }
                        role="button"
                        tabIndex={0}
                        aria-label={`Xem ${stopLabel(stop)} trên bản đồ`}
                        onClick={() => handleSelectPosition(pinNumber)}
                        onMouseEnter={() => setHoveredPin(pinNumber)}
                        onMouseLeave={() =>
                          setHoveredPin((cur) => (cur === pinNumber ? null : cur))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleSelectPosition(pinNumber);
                          }
                        }}
                      >
                        <span className="timeline__rail" aria-hidden="true">
                          {isVisit ? (
                            <span className="timeline__marker">{markers[stopIndex]}</span>
                          ) : (
                            <span className="timeline__marker timeline__marker--anchor">
                              {markers[stopIndex]}
                            </span>
                          )}
                          <span className="timeline__connector" />
                        </span>

                        <div className="timeline__content">
                          {(isVisit || stop.kind === "lodging" || stop.kind === "entry") &&
                          stop.place_id !== null ? (
                            <Link
                              className="timeline__link"
                              to={`/places/${stop.place_id}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {stopLabel(stop)}
                            </Link>
                          ) : (
                            <span className="timeline__name">{stopLabel(stop)}</span>
                          )}

                          <span className="timeline__note">
                            {stop.start_min === null
                              ? null
                              : `${formatClock(stop.start_min)} · `}
                            {stop.duration_min === null
                              ? copy.state.unknown
                              : `${stop.duration_min} phút`}
                          </span>

                          {(() => {
                            const earlier = (stop.place_id === null
                              ? []
                              : (daysByPlace.get(stop.place_id) ?? [])
                            ).filter((index) => index < day.day_index);
                            if (earlier.length === 0) return null;
                            return (
                              <span className="timeline__repeat">
                                ↺ đã đến ngày {earlier.join(", ")}
                              </span>
                            );
                          })()}
                        </div>

                        {leg !== undefined && (
                          <div className="timeline__leg">
                            🚗 {formatKm(leg.distance_m)} · {formatDriving(leg.duration_s)} di chuyển
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Right Column: Day-Specific Map (visible on desktop) */}
              <div className="plan-day__map-wrapper">
                {pins.length > 0 ? (
                  <RouteMap
                    stops={pins}
                    geometry={day.geometry}
                    legs={legs}
                    distancesKnown={dayDistancesKnown(day)}
                    highlightedPosition={activePin}
                    onHoverPosition={setHoveredPin}
                    onSelectPosition={handleSelectPosition}
                  />
                ) : (
                  <div className="plan-day__empty">Ngày này không có điểm dừng nào trên bản đồ.</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function PlanRoadmap({ detail, startDate, hideSummary = false }: PlanRoadmapProps): ReactElement {
  /**
   * Which days each place appears on, so a repeat can be marked where it is read.
   */
  const daysByPlace = new Map<string, number[]>();
  for (const day of detail.days) {
    for (const stop of day.stops) {
      if (stop.place_id === null) continue;
      const seen = daysByPlace.get(stop.place_id) ?? [];
      if (!seen.includes(day.day_index)) seen.push(day.day_index);
      daysByPlace.set(stop.place_id, seen);
    }
  }

  return (
    <div className="plan-step">
      {!hideSummary && (
        <div className="plan-summary">
          <p className="plan-summary__title">{copy.trips.chosenPlan(detail.variant.label)}</p>
          <dl className="figures figures--strip">
            {[
              { value: String(detail.variant.place_count), label: "điểm" },
              { value: formatKm(detail.variant.total_distance_m), label: "quãng đường" },
              { value: formatDriving(detail.variant.total_driving_s), label: "lái xe" },
              { value: formatRestHours(detail.variant.total_rest_s), label: "nghỉ" },
            ].map((number) => (
              <div key={number.label} className="figure">
                <dd className="figure__value figure__value--strip">{number.value}</dd>
                <dt className="figure__label">{number.label}</dt>
              </div>
            ))}
          </dl>
          {detail.variant.unsatisfied.length > 0 && (
            <div className="alert alert--warning" role="alert">
              {copy.planner.unsatisfied} {detail.variant.unsatisfied.join("; ")}
            </div>
          )}
        </div>
      )}

      {!detail.days.every(dayDistancesKnown) && (
        <div className="alert alert--warning" role="alert">
          {copy.planner.routingUnavailable}
        </div>
      )}

      {/*
        One map per day, and the cost of that is accepted.

        Measured on a three-day plan: three `RouteMap`s mount, so three Leaflet instances and three tile
        layers. The redesign's own comment recorded three as the number worth worrying about on a mid-range
        Android, and the owner has since accepted one map per day — the per-day map is the feature, and the
        alternative that was weighed and declined was a single map switching between days.

        Hiding a map does not avoid its cost. Below 760px the list-view wrapper is `display: none`, but the
        component still mounts and its Leaflet effect still runs, so a phone pays for a map it cannot see —
        the accepted price of one component rather than a second hidden-map code path.

        If it becomes a problem — jank on a low-end device, or a plan long enough that the tile requests are
        felt — the change is to mount only the open day's map, not to cap the plan: the day count is the
        user's, not this component's.
      */}
      <div className="plan-stack">
        {detail.days.map((day) => (
          <PlanDayBlock
            key={day.day_index}
            day={day}
            startDate={startDate ?? null}
            daysByPlace={daysByPlace}
          />
        ))}
      </div>
    </div>
  );
}
